import { gql } from '@apollo/client';

export const GET_CHAT_ROOMS = gql`
  query GetChatRooms($userId: String!) {
    findManyChatRooms(args: { where: { userId: $userId }, include: { lastMessage: true }, orderBy: { updatedAt: "desc" } }) {
      id
      name
      userId
      agentId
      lastMessageId
      lastMessage {
        id
        text
        role
        createdAt
      }
      createdAt
      updatedAt
    }
  }
`;

export const GET_CHAT_ROOMS_PAGINATED = gql`
  query GetChatRoomsPaginated($userId: String!, $skip: Int!, $take: Int!) {
    findManyChatRooms(
      args: { where: { userId: $userId }, include: { lastMessage: true }, orderBy: { updatedAt: "desc" }, skip: $skip, take: $take }
    ) {
      id
      name
      userId
      agentId
      lastMessageId
      lastMessage {
        id
        text
        role
        createdAt
      }
      createdAt
      updatedAt
    }
  }
`;

export const GET_MESSAGES_BY_CHATROOM = gql`
  query GetMessagesByChatRoom($chatRoomId: String!, $userId: String!) {
    findManyMessages(
      args: { where: { chatRoomId: $chatRoomId, userId: $userId }, orderBy: { createdAt: "asc" }, include: { sourceDocuments: true } }
    ) {
      id
      text
      role
      userId
      chatRoomId
      rating
      feedback
      contextDocumentIds
      contextFormIds
      thinkingSteps
      contentBlocks
      createdAt
      updatedAt
      sourceDocuments {
        id
        title
        content
        score
        metadata
        messageId
        createdAt
        updatedAt
        userId
      }
    }
  }
`;

export const GET_FOLDERS = gql`
  query GetFolders($userId: String!) {
    findManyFolders(args: { where: { userId: $userId }, orderBy: { createdAt: "desc" }, include: { subFolders: true, documents: true } }) {
      id
      name
      description
      createdAt
      updatedAt
      parentId
      userId
      documents {
        id
        title
        extension
        fileSize
        status
        folderId
        userId
        source
        text
        originalMarkdownMd5
        processedMarkdownMd5
        sourceDocumentMd5
        sourceFilename
        sourceType
        sourceFileS3Key
        parsedAt
        assets
        regions
        createdAt
        updatedAt
      }
      subFolders {
        id
        name
        description
        createdAt
        updatedAt
        parentId
        userId
      }
    }
  }
`;

export const GET_ALL_FOLDERS_FOR_PICKER = gql`
  query GetAllFoldersForPicker {
    findManyFolders(
      args: { where: { deletedAt: null }, orderBy: { name: "asc" }, include: { documents: { where: { deletedAt: null, status: "COMPLETED" } } } }
    ) {
      id
      name
      parentId
      documents {
        id
        title
        sourceFilename
      }
    }
  }
`;

export const SEARCH_DOCUMENTS_BY_CONTENT = gql`
  query SearchDocumentsByContent($query: String!, $take: Int = 50) {
    findManyDocuments(
      args: {
        where: { AND: [{ text: { contains: $query, mode: "insensitive" } }, { status: { equals: "COMPLETED" } }, { deletedAt: { equals: null } }] }
        take: $take
        orderBy: { title: "asc" }
      }
    ) {
      id
      title
      sourceFilename
      folderId
    }
  }
`;

export const CREATE_ROOT_FOLDER = gql`
  mutation CreateRootFolder($name: String!, $description: String!, $userId: String!) {
    createFolder(args: { data: { name: $name, description: $description, userId: $userId, parentId: null } }) {
      id
      name
      description
      parentId
      userId
      createdAt
      updatedAt
    }
  }
`;

export const CREATE_FOLDER = gql`
  mutation CreateFolder($name: String!, $description: String!, $userId: String!, $parentId: String) {
    createFolder(args: { data: { name: $name, description: $description, userId: $userId, parentId: $parentId } }) {
      id
      name
      description
      parentId
      userId
      createdAt
      updatedAt
    }
  }
`;

export const GET_DOCUMENTS = gql`
  query GetDocuments($folderId: String, $userId: String!, $page: Int, $pageSize: Int) {
    findManyDocuments(args: { where: { folderId: $folderId, userId: $userId }, orderBy: { createdAt: "desc" }, skip: $page, take: $pageSize }) {
      id
      title
      extension
      fileSize
      status
      folderId
      userId
      source
      text
      originalMarkdownMd5
      processedMarkdownMd5
      sourceDocumentMd5
      sourceFilename
      sourceType
      sourceFileS3Key
      parsedAt
      assets
      regions
      createdAt
      updatedAt
    }
  }
`;

export const SOFT_DELETE_DOCUMENT = gql`
  mutation SoftDeleteDocument($id: String!, $userId: String!) {
    softDeleteDocument(id: $id, userId: $userId) {
      id
      deletedAt
    }
  }
`;

export const UPDATE_DOCUMENT = gql`
  mutation UpdateDocument($id: String!, $userId: String!, $data: JSONObject!) {
    updateDocument(args: { where: { id: $id, userId: $userId }, data: $data }) {
      id
      title
      status
      updatedAt
    }
  }
`;

export const GET_LANGUAGES = gql`
  query GetLanguages {
    findManyLanguages(args: { orderBy: { name: "asc" } }) {
      id
      code
      name
    }
  }
`;

export const GET_AGENTS = gql`
  query GetAgents {
    findManyAgents(
      args: {
        orderBy: { createdAt: "desc" }
        include: { language: true, knowledgeGraph: true, agentFolders: true, workflow: true }
      }
    ) {
      id
      name
      agentType
      audience
      userId
      languageId
      knowledgeGraphId
      hidden
      formFolderPath
      isMultiagent
      workflowId
      workflow {
        id
        name
        definition
      }
      knowledgeGraph {
        id
        namespace
        version
        status
        isActive
        folderSnapshot
      }
      agentFolders {
        agentId
        folderId
      }
      language {
        id
        name
        code
        createdAt
        updatedAt
      }
      dataStatus
      dataStatusAt
      dataStatusReason
      status
      createdAt
      updatedAt
    }
  }
`;

export const GET_SELECTABLE_AGENTS = gql`
  query GetSelectableAgents($audience: AgentAudience = PUBLIC) {
    getUserAgents(query: { audience: $audience, status: COMPLETED, isActive: true, hidden: false }) {
      id
      name
      agentType
      audience
      status
      knowledgeGraphId
      workflowId
      workflow {
        id
        definition
      }
      language {
        id
        name
        code
      }
      createdAt
      updatedAt
    }
  }
`;

export const TOGGLE_AGENT_HIDDEN = gql`
  mutation ToggleAgentHidden($id: String!) {
    toggleAgentHidden(id: $id) {
      id
      hidden
    }
  }
`;

export const CREATE_CHAT_ROOM = gql`
  mutation CreateChatRoom($name: String!, $userId: String!, $agentId: String!, $agentName: String) {
    createChatRoom(args: { data: { name: $name, userId: $userId, agentId: $agentId, agentName: $agentName } }) {
      id
      name
      userId
      agentId
      createdAt
      updatedAt
      agent {
        id
        name
        workflowId
        workflow {
          id
          definition
        }
      }
    }
  }
`;

export const DELETE_CHAT_ROOM_MESSAGES = gql`
  mutation DeleteChatRoomMessages($chatRoomId: String!, $userId: String!) {
    deleteManyMessages(args: { where: { chatRoomId: $chatRoomId, userId: $userId } }) {
      count
    }
  }
`;

export const DELETE_CHAT_ROOM = gql`
  mutation DeleteChatRoom($id: String!, $userId: String!) {
    deleteChatRoom(args: { where: { id: $id, userId: $userId } }) {
      id
      name
      userId
      lastMessageId
      createdAt
      updatedAt
    }
  }
`;

export const GET_USER_INFO = gql`
  query GetUserInfo($id: String) {
    findMyFirstUser(args: { where: { id: $id } }) {
      id
      email
      username
      firstName
      lastName
      role
      createdAt
      updatedAt
    }
  }
`;

export const UPDATE_USER_SETTINGS = gql`
  mutation UpdateUserSettings($id: String!, $firstName: String, $lastName: String) {
    updateMyUser(args: { where: { id: $id }, data: { firstName: $firstName, lastName: $lastName } }) {
      id
      email
      username
      firstName
      lastName
      role
      createdAt
      updatedAt
    }
  }
`;

export const GET_SOURCE_DOCUMENTS = gql`
  query GetSourceDocuments($messageId: String!, $userId: String!) {
    findManySourceDocuments(args: { where: { messageId: $messageId, userId: $userId }, orderBy: { score: "desc" }, include: { message: true } }) {
      id
      title
      content
      score
      metadata
      messageId
      userId
      message {
        id
        text
        role
      }
      createdAt
      updatedAt
    }
  }
`;

export const RATE_MESSAGE = gql`
  mutation RateMessage($messageId: String!, $userId: String!, $rating: Int!, $feedback: String) {
    updateMessage(args: { where: { id: $messageId, userId: $userId }, data: { rating: $rating, feedback: $feedback } }) {
      id
      rating
      feedback
      updatedAt
    }
  }
`;

export const GET_AGENT_BY_ID = gql`
  query GetAgentById($id: String!) {
    findFirstAgent(
      args: {
        where: { id: { equals: $id } }
        include: { language: true, knowledgeGraph: true, agentFolders: true, workflow: true }
      }
    ) {
      id
      name
      agentType
      audience
      languageId
      knowledgeGraphId
      hidden
      formFolderPath
      isMultiagent
      workflowId
      workflow {
        id
        name
        definition
      }
      knowledgeGraph {
        id
        namespace
        version
        status
      }
      agentFolders {
        agentId
        folderId
      }
      language {
        id
        code
        name
      }
      status
      createdAt
      updatedAt
    }
  }
`;

export const GET_USERS = gql`
  query GetUsers {
    findManyUsers(args: { where: { platformUserId: null }, orderBy: { createdAt: "desc" } }) {
      id
      email
      username
      firstName
      lastName
      role
      status
      createdAt
      updatedAt
    }
  }
`;

export const GET_USERS_PAGINATED = gql`
  query GetUsersPaginated($skip: Int!, $take: Int!, $where: JSON) {
    findManyUsers(args: { where: $where, orderBy: { createdAt: "desc" }, skip: $skip, take: $take }) {
      id
      email
      username
      firstName
      lastName
      role
      status
      createdAt
      updatedAt
    }
  }
`;

export const COUNT_USERS = gql`
  mutation CountUsers($where: JSON) {
    countUsers(args: { where: $where })
  }
`;

export const CREATE_USER = gql`
  mutation CreateUser($email: String!, $username: String!, $firstName: String, $lastName: String, $role: String!) {
    createUser(args: { data: { email: $email, username: $username, firstName: $firstName, lastName: $lastName, role: $role } }) {
      id
      email
      username
      firstName
      lastName
      role
      status
      createdAt
      updatedAt
    }
  }
`;

export const UPDATE_USER = gql`
  mutation UpdateUser($id: String!, $data: JSONObject!) {
    updateUser(args: { where: { id: $id }, data: $data }) {
      id
      email
      username
      firstName
      lastName
      role
      status
      createdAt
      updatedAt
    }
  }
`;

export const DELETE_USER = gql`
  mutation DeleteUser($id: String!) {
    deleteUser(args: { where: { id: $id } }) {
      id
      email
      username
    }
  }
`;

export const DISABLE_USER = gql`
  mutation DisableUser($userId: String!) {
    disableUser(userId: $userId) {
      id
      email
      username
      firstName
      lastName
      role
      status
      createdAt
      updatedAt
    }
  }
`;

export const ENABLE_USER = gql`
  mutation EnableUser($userId: String!) {
    enableUser(userId: $userId) {
      id
      email
      username
      firstName
      lastName
      role
      status
      createdAt
      updatedAt
    }
  }
`;

export const ENSURE_KG_AGENT = gql`
  mutation EnsureKgAgent($args: JSONObject!) {
    ensureKgAgent(args: $args) {
      id
      name
      knowledgeGraphId
      workflowId
      workflow {
        id
        definition
      }
      knowledgeGraph {
        id
        namespace
        version
        status
      }
      agentFolders {
        agentId
        folderId
      }
    }
  }
`;

// Knowledge Graph specific queries
export const GET_KG_STATUS = gql`
  query GetKGStatus($userId: String!, $namespace: String!) {
    findKnowledgeGraphByUserNamespaceAndType(
      args: { where: { userId: $userId, namespace: $namespace, workflowType: "IMPORT" }, orderBy: { createdAt: "desc" }, take: 1 }
    ) {
      id
      namespace
      version
      status
      workflowType
      totalSteps
      completedSteps
      progressData
      completedAt
      errorMessage
      createdAt
      pickleMd5
      knowledgeGraphDocuments {
        id
        documentId
        nodeCount
        relationshipCount
        document {
          id
          title
          status
        }
      }
    }
  }
`;

export const GET_KG_IMPORTS_HISTORY = gql`
  query GetKGImportsHistory($userId: String!, $namespace: String!, $limit: Int) {
    findKnowledgeGraphsByUserAndNamespace(
      args: { where: { userId: $userId, namespace: $namespace, workflowType: "IMPORT" }, orderBy: { createdAt: "desc" }, take: $limit }
    ) {
      id
      namespace
      version
      status
      workflowType
      totalSteps
      completedSteps
      progressData
      completedAt
      errorMessage
      createdAt
      pickleMd5
      knowledgeGraphDocuments {
        id
        documentId
        nodeCount
        relationshipCount
        document {
          id
          title
          status
        }
      }
    }
  }
`;

export const GET_KG_COMPOSITION = gql`
  query GetKGComposition($userId: String!, $namespace: String!) {
    findActiveKnowledgeGraphsByUser(args: { userId: $userId, namespace: $namespace }) {
      id
      namespace
      version
      status
      progressData
      completedAt
      createdAt
      knowledgeGraphDocuments {
        id
        documentId
        nodeCount
        relationshipCount
        document {
          id
          title
          status
        }
      }
    }
  }
`;

export const GET_KNOWLEDGE_GRAPHS_BY_USER_AND_NAMESPACE = gql`
  query GetKnowledgeGraphsByUserAndNamespace($userId: String!, $namespace: String!) {
    findKnowledgeGraphsByUserAndNamespace(args: { userId: $userId, namespace: $namespace }) {
      id
      userId
      workflowType
      sourceType
      namespace
      version
      status
      totalSteps
      completedSteps
      progressData
      validationTree
      pickleMd5
      errorMessage
      createdAt
      completedAt
      knowledgeGraphDocuments {
        id
        documentId
        nodeCount
        relationshipCount
        document {
          id
          title
          status
        }
      }
    }
  }
`;

export const GET_KNOWLEDGE_GRAPH_BY_USER_NAMESPACE_AND_TYPE = gql`
  query GetKnowledgeGraphByUserNamespaceAndType($userId: String!, $namespace: String!, $workflowType: String!) {
    findKnowledgeGraphByUserNamespaceAndType(args: { userId: $userId, namespace: $namespace, workflowType: $workflowType }) {
      id
      userId
      workflowType
      sourceType
      namespace
      version
      status
      totalSteps
      completedSteps
      progressData
      validationTree
      pickleMd5
      errorMessage
      createdAt
      completedAt
      knowledgeGraphDocuments {
        id
        documentId
        nodeCount
        relationshipCount
        document {
          id
          title
          status
        }
      }
    }
  }
`;

export const GET_KNOWLEDGE_GRAPH_DOCUMENT_STATS = gql`
  query GetKnowledgeGraphDocumentStats($knowledgeGraphId: String!) {
    getKnowledgeGraphDocumentStats(knowledgeGraphId: $knowledgeGraphId)
  }
`;

export const GET_DOCUMENTS_BY_KNOWLEDGE_GRAPH_ID = gql`
  query GetDocumentsByKnowledgeGraphId($knowledgeGraphId: String!) {
    getDocumentsByKnowledgeGraphId(knowledgeGraphId: $knowledgeGraphId) {
      id
      documentId
      nodeCount
      relationshipCount
      document {
        id
        title
        status
      }
    }
  }
`;

// Optimized KG queries with pagination support
export const GET_KG_DOCUMENT_COUNT = gql`
  mutation GetKGDocumentCount($knowledgeGraphId: String!) {
    countKnowledgeGraphDocuments(args: { where: { knowledgeGraphId: $knowledgeGraphId } })
  }
`;

export const GET_KG_DOCUMENTS_PAGINATED = gql`
  query GetKGDocumentsPaginated($knowledgeGraphId: String!, $skip: Int!, $take: Int!, $searchQuery: String) {
    findManyKnowledgeGraphDocuments(
      args: {
        where: { knowledgeGraphId: $knowledgeGraphId, document: { title: { contains: $searchQuery, mode: "insensitive" } } }
        skip: $skip
        take: $take
        orderBy: { createdAt: "desc" }
        include: { document: true }
      }
    ) {
      id
      documentId
      nodeCount
      relationshipCount
      document {
        id
        title
        status
        fileSize
        extension
        sourceFilename
        sourceType
        sourceDocumentMd5
        parsedAt
        createdAt
        updatedAt
      }
    }
  }
`;

export const GET_FOLDERS_WITH_DOCUMENT_COUNT = gql`
  query GetFoldersWithDocumentCount {
    findManyFolders(
      args: {
        where: { deletedAt: null, user: { role: { equals: "ADMIN" } } }
        orderBy: { createdAt: "desc" }
        include: {
          _count: { select: { documents: { where: { deletedAt: null, user: { role: { equals: "ADMIN" } } } }, subFolders: true } }
          subFolders: {
            where: { deletedAt: null, user: { role: { equals: "ADMIN" } } }
            select: { id: true, name: true, _count: { select: { documents: { where: { deletedAt: null, user: { role: { equals: "ADMIN" } } } } } } }
          }
        }
      }
    ) {
      id
      name
      description
      createdAt
      updatedAt
      parentId
      userId
      _count {
        documents
        subFolders
      }
      subFolders {
        id
        name
        _count {
          documents
        }
      }
    }
  }
`;

export const GET_FOLDER_DOCUMENTS_PAGINATED = gql`
  query GetFolderDocumentsPaginated($folderId: String, $skip: Int!, $take: Int!, $searchQuery: String) {
    findManyDocuments(
      args: {
        where: { folderId: $folderId, deletedAt: null, user: { role: { equals: "ADMIN" } }, title: { contains: $searchQuery, mode: "insensitive" } }
        orderBy: { createdAt: "desc" }
        skip: $skip
        take: $take
      }
    ) {
      id
      title
      extension
      fileSize
      status
      folderId
      userId
      source
      text
      originalMarkdownMd5
      processedMarkdownMd5
      sourceDocumentMd5
      sourceFilename
      sourceType
      sourceFileS3Key
      parsedAt
      assets
      regions
      createdAt
      updatedAt
    }
  }
`;

// KG Status optimized for counts only - namespace-based shared access
export const GET_KG_STATUS_OPTIMIZED = gql`
  query GetKGStatusOptimized($namespace: String!) {
    findKnowledgeGraphsByNamespace(args: { namespace: $namespace }) {
      id
      namespace
      version
      status
      workflowType
      totalSteps
      completedSteps
      progressData
      completedAt
      errorMessage
      createdAt
      pickleMd5
      totalNodes
      totalRelationships
      _count {
        knowledgeGraphDocuments
      }
    }
  }
`;

// Fragments for reusable query parts
export const KG_DOCUMENT_FRAGMENT = gql`
  fragment KGDocumentFragment on KnowledgeGraphDocument {
    id
    documentId
    nodeCount
    relationshipCount
    document {
      id
      title
      status
      fileSize
      extension
      sourceFilename
      sourceType
      sourceDocumentMd5
      parsedAt
      createdAt
      updatedAt
    }
  }
`;

export const FOLDER_COUNT_FRAGMENT = gql`
  fragment FolderCountFragment on Folder {
    id
    name
    description
    _count {
      documents
      subFolders
    }
  }
`;

// Optimized KG document queries for pagination and performance
export const GET_KG_DOCUMENTS_COUNT = gql`
  mutation GetKGDocumentsCount($knowledgeGraphId: String!) {
    countKnowledgeGraphDocuments(args: { where: { knowledgeGraphId: $knowledgeGraphId } })
  }
`;

// Lightweight metrics query for counts only using existing resolvers
export const GET_KG_METRICS_LIGHTWEIGHT = gql`
  query GetKGMetricsLightweight($knowledgeGraphId: String!) {
    getKnowledgeGraphDocumentStats(knowledgeGraphId: $knowledgeGraphId)
  }
`;

// Get all models
export const GET_ALL_MODELS = gql`
  query GetAllModels {
    findManyModels(args: { orderBy: { modelType: "asc" } }) {
      id
      code
      name
      description
      modelType
      provider
      isActive
      isDefault
      isFallback
      fallbackModelId
      fallbackModel {
        id
        code
        name
        provider
        modelType
        parameters
      }
      parameters
      createdAt
      updatedAt
    }
  }
`;

// Get models by type
export const GET_MODELS_BY_TYPE = gql`
  query GetModelsByType($modelType: ModelType!) {
    findManyModels(args: { where: { modelType: $modelType }, orderBy: { isDefault: "desc" } }) {
      id
      code
      name
      description
      modelType
      provider
      isActive
      isDefault
      isFallback
      fallbackModelId
      fallbackModel {
        id
        code
        name
        provider
        modelType
        parameters
      }
      parameters
      createdAt
      updatedAt
    }
  }
`;

// Update model (admin only)
export const UPDATE_MODEL = gql`
  mutation UpdateModel($id: String!, $data: JSONObject!) {
    updateModel(args: { where: { id: $id }, data: $data }) {
      id
      code
      name
      description
      modelType
      provider
      isActive
      isDefault
      isFallback
      fallbackModelId
      fallbackModel {
        id
        code
        name
        provider
        modelType
        parameters
      }
      parameters
      createdAt
      updatedAt
    }
  }
`;

export const GET_DOCUMENT_CONTENT = gql`
  query GetDocumentContent($id: String!, $userId: String!) {
    findManyDocuments(args: { where: { id: $id, deletedAt: null, OR: [{ userId: { equals: $userId } }, { user: { role: { equals: "ADMIN" } } }] }, take: 1 }) {
      id
      title
      text
      source
      sourceFileS3Key
    }
  }
`;

export const GET_DOCUMENT_VERSIONS = gql`
  query GetDocumentVersions($documentId: String!, $take: Int!) {
    getDocumentVersions(documentId: $documentId, take: $take) {
      id
      md5Hash
      changeType
      changedBy
      createdAt
    }
  }
`;

// ── FormTemplate (admin Forms list) ────────────────────────────────
//
// Namespace is JWT-derived server-side — we never pass it from the client.
// `meta` is JSON; the hook's normalizer pulls `slotCount` / `category` /
// `title` / `sizeBytes` out of `meta` and maps `formId` → `filename`.
export const FIND_FORM_TEMPLATES_PAGINATED = gql`
  query FindFormTemplatesPaginated($input: FindFormTemplatesInput) {
    findFormTemplatesPaginated(input: $input) {
      items {
        id
        formId
        folderId
        md5
        version
        meta
        authoredBy
        createdAt
        updatedAt
      }
      total
    }
  }
`;

/** List every FormFolder in the JWT-bound namespace. Used by the @-picker
 *  Forms tab to build the folder-tree grouping, and by Forms admin to
 *  resolve `folderId → human label`. */
export const FIND_MANY_FORM_FOLDERS = gql`
  query FindManyFormFolders($args: JSONObject!) {
    findManyFormFolders(args: $args) {
      id
      namespace
      name
      parentId
      createdAt
      updatedAt
    }
  }
`;

/**
 * Per-folder FormTemplate counts for the JWT-bound namespace. Powers the
 * folder badges on /admin/forms + the @-picker — one grouped SQL query
 * server-side, ~100 ints over the wire. Lets the FE render the folder
 * tree without fetching templates. `folderId === ""` is the namespace
 * root bucket (rows with `folderId IS NULL`).
 */
export const FIND_FORM_TEMPLATE_COUNTS_BY_FOLDER = gql`
  query FindFormTemplateCountsByFolder {
    findFormTemplateCountsByFolder {
      folderId
      count
    }
  }
`;

export const FIND_FORM_PARSED_CONTENT = gql`
  query FindFormParsedContent($args: JSONObject!) {
    findUniqueFormParsedContent(args: $args) {
      id
      formTemplateId
      text
      originalMarkdownMd5
      parsedAt
      assets
    }
  }
`;

export const SOFT_DELETE_FORM_TEMPLATE = gql`
  mutation SoftDeleteFormTemplate($id: String!) {
    softDeleteFormTemplate(id: $id) {
      id
      formId
      meta
      updatedAt
    }
  }
`;

/** Restore a soft-deleted FormTemplate. Inverse of `SOFT_DELETE_FORM_TEMPLATE`.
 *  Clears `meta.deleted` + `meta.deletedAt`. Idempotent on already-active rows. */
export const RESTORE_FORM_TEMPLATE = gql`
  mutation RestoreFormTemplate($id: String!) {
    restoreFormTemplate(id: $id) {
      id
      formId
      meta
      updatedAt
    }
  }
`;

/** Move a FormTemplate to a different FormFolder within the same namespace.
 *  Preserves FormTemplate.id (no churn on FormParsedContent /
 *  Message.contextFormIds). 409 if the target folder already has a
 *  template with the same basename. */
export const MOVE_FORM_TEMPLATE_TO_FOLDER = gql`
  mutation MoveFormTemplateToFolder($id: String!, $folderId: String!) {
    moveFormTemplateToFolder(id: $id, folderId: $folderId) {
      id
      formId
      folderId
      meta
      updatedAt
    }
  }
`;

// ── API Key Management (from citeka/ifc) ────────────────────────────────────

export const GET_API_KEYS = gql`
  query GetApiKeys($filter: ApiKeyFilterInput) {
    apiKeys(filter: $filter) {
      id
      keyPrefix
      name
      agentId
      scopes
      rateLimit
      dailyLimit
      status
      usageCount
      lastUsedAt
      createdAt
      expiresAt
    }
  }
`;

export const GET_API_KEY = gql`
  query GetApiKey($id: ID!) {
    apiKey(id: $id) {
      id
      keyPrefix
      name
      agentId
      scopes
      rateLimit
      dailyLimit
      status
      usageCount
      lastUsedAt
      createdAt
      expiresAt
    }
  }
`;

export const CREATE_API_KEY = gql`
  mutation CreateApiKey($input: CreateApiKeyInput!) {
    createApiKey(input: $input) {
      apiKey {
        id
        keyPrefix
        name
        agentId
        scopes
        rateLimit
        dailyLimit
        status
        createdAt
        expiresAt
      }
      rawKey
    }
  }
`;

export const UPDATE_API_KEY = gql`
  mutation UpdateApiKey($id: ID!, $input: UpdateApiKeyInput!) {
    updateApiKey(id: $id, input: $input) {
      id
      keyPrefix
      name
      agentId
      scopes
      rateLimit
      dailyLimit
      status
      usageCount
      lastUsedAt
      createdAt
      expiresAt
    }
  }
`;

export const DELETE_API_KEY = gql`
  mutation DeleteApiKey($id: ID!) {
    deleteApiKey(id: $id) {
      success
      message
      apiKeyId
    }
  }
`;

export const REVOKE_API_KEY = gql`
  mutation RevokeApiKey($id: ID!) {
    revokeApiKey(id: $id) {
      success
      message
      apiKeyId
    }
  }
`;

// ── OneDrive Integration (from citeka) ──────────────────────────────────────

export const GET_HIDDEN_ONEDRIVE_ITEMS = gql`
  query GetHiddenOneDriveItems {
    hiddenOneDriveItems {
      id
      userId
      path
      hiddenAt
    }
  }
`;

export const GET_HIDDEN_ONEDRIVE_PATHS = gql`
  query GetHiddenOneDrivePaths {
    hiddenOneDrivePaths
  }
`;

export const HIDE_ONEDRIVE_ITEM = gql`
  mutation HideOneDriveItem($path: String!) {
    hideOneDriveItem(path: $path) {
      id
      userId
      path
      hiddenAt
    }
  }
`;

export const UNHIDE_ONEDRIVE_ITEM = gql`
  mutation UnhideOneDriveItem($path: String!) {
    unhideOneDriveItem(path: $path)
  }
`;

export const UNHIDE_ALL_ONEDRIVE_ITEMS = gql`
  mutation UnhideAllOneDriveItems {
    unhideAllOneDriveItems
  }
`;

export const CLEANUP_ORPHANED_HIDDEN_ITEMS = gql`
  mutation CleanupOrphanedHiddenItems($validPaths: [String!]!) {
    cleanupOrphanedHiddenItems(validPaths: $validPaths)
  }
`;

export const GET_ORIGINAL_DOCUMENTS = gql`
  query GetOriginalDocuments($userId: String!, $folderId: String) {
    findManyOriginalDocuments(
      args: { where: { userId: $userId, folderId: $folderId }, orderBy: { syncedAt: "desc" }, include: { user: true, folder: true } }
    ) {
      id
      fileName
      filePath
      fileExtension
      fileSize
      mimeType
      md5Hash
      oneDriveItemId
      webUrl
      downloadUrl
      syncedAt
      lastModifiedAt
      status
      userId
      folderId
      metadata
      createdAt
      updatedAt
      user {
        id
        username
        email
      }
      folder {
        id
        name
      }
    }
  }
`;

export const GET_ORIGINAL_DOCUMENT = gql`
  query GetOriginalDocument($id: String!) {
    findUniqueOriginalDocument(args: { where: { id: $id }, include: { user: true, folder: true } }) {
      id
      fileName
      filePath
      fileExtension
      fileSize
      mimeType
      md5Hash
      oneDriveItemId
      webUrl
      downloadUrl
      syncedAt
      lastModifiedAt
      status
      userId
      folderId
      metadata
      createdAt
      updatedAt
      user {
        id
        username
        email
        firstName
        lastName
      }
      folder {
        id
        name
        description
      }
    }
  }
`;

export const COUNT_ORIGINAL_DOCUMENTS = gql`
  query CountOriginalDocuments($userId: String!, $folderId: String) {
    countOriginalDocuments(args: { where: { userId: $userId, folderId: $folderId } })
  }
`;

export const GET_USER_DOCUMENTS_FOR_PICKER = gql`
  query GetUserDocumentsForPicker($userId: String!, $skip: Int!, $take: Int!, $searchQuery: String) {
    findManyDocuments(
      args: {
        where: { userId: $userId, title: { contains: $searchQuery, mode: "insensitive" } }
        orderBy: { createdAt: "desc" }
        skip: $skip
        take: $take
      }
    ) {
      id
      title
      extension
      fileSize
      status
      folderId
      createdAt
    }
  }
`;

// ── Agent Sync Configuration (from ueh_chatbot) ──────────────────────────────

export const CREATE_AGENT = gql`
  mutation CreateAgent($args: JSONObject!) {
    createAgent(args: $args) {
      id
      name
      agentType
      userId
      languageId
      workflowId
      status
      createdAt
      updatedAt
    }
  }
`;

export const CREATE_AGENT_SYNC_CONFIG = gql`
  mutation CreateAgentSyncConfig($args: JSONObject!) {
    createAgentSyncConfig(args: $args) {
      id
      agentId
      syncUrls
      folderId
      autoUpdate
      lastSyncAt
      createdAt
      updatedAt
    }
  }
`;

export const UPDATE_AGENT_SYNC_CONFIG = gql`
  mutation UpdateAgentSyncConfig($args: JSONObject!) {
    updateAgentSyncConfig(args: $args) {
      id
      agentId
      syncUrls
      folderId
      autoUpdate
      lastSyncAt
      createdAt
      updatedAt
    }
  }
`;

export const DELETE_AGENT_SYNC_CONFIG = gql`
  mutation DeleteAgentSyncConfig($args: JSONObject!) {
    deleteAgentSyncConfig(args: $args) {
      id
    }
  }
`;

export const GET_AGENT_SYNC_CONFIGS = gql`
  query GetAgentSyncConfigs {
    findManyAgentSyncConfigs(args: { include: { agent: true, targetFolder: true }, orderBy: { createdAt: "desc" } }) {
      id
      agentId
      syncUrls
      folderId
      autoUpdate
      lastSyncAt
      createdAt
      updatedAt
      agent {
        id
        name
        status
        createdAt
        updatedAt
      }
      targetFolder {
        id
        name
        description
        userId
        createdAt
        updatedAt
      }
    }
  }
`;

export const RECREATE_AGENT = gql`
  mutation RecreateAgent($agentId: String!) {
    recreateAgent(agentId: $agentId) {
      config {
        name
        languageId
        folderIds
        tools
      }
      userId
    }
  }
`;

export const UPDATE_AGENT_CONFIG = gql`
  mutation UpdateAgentConfig($agentId: String!, $updates: JSONObject!) {
    updateAgent(args: { where: { id: $agentId }, data: $updates }) {
      id
      name
      status
      agentType
      languageId
      userId
      workflowId
      createdAt
      updatedAt
    }
  }
`;

export const UPDATE_AGENT_FOLDERS = gql`
  mutation UpdateAgentFolders($agentId: String!, $folderIds: [String!]!) {
    updateAgentFolders(agentId: $agentId, folderIds: $folderIds) {
      id
      name
      status
      agentType
      languageId
      userId
      workflowId
      createdAt
      updatedAt
    }
  }
`;

export const UPDATE_AGENT_STATUS = gql`
  mutation UpdateAgentStatus($agentId: String!, $status: String!) {
    updateAgent(args: { where: { id: $agentId }, data: { status: $status } }) {
      id
      name
      status
      createdAt
      updatedAt
    }
  }
`;

// ── URL Configuration (from ueh_chatbot) ────────────────────────────────────

export const GET_ALL_URL_CONFIGS = gql`
  query GetAllUrlConfigs {
    getAllUrlConfigs {
      id
      displayUrl
      apiEndpoint
      apiClientId
      folderName
      folderId
      lastSyncAt
      folder {
        id
        name
        description
        createdAt
      }
      createdAt
      updatedAt
    }
  }
`;

export const GET_URL_CONFIG_DETAILS = gql`
  query GetUrlConfigDetails($id: String!) {
    findUniqueUrlConfig(args: { where: { id: $id }, include: { folder: { include: { documents: true } } } }) {
      id
      displayUrl
      apiEndpoint
      folderName
      folderId
      lastSyncAt
      apiClientId
      folder {
        id
        name
        description
        documents {
          id
          title
          status
          createdAt
          updatedAt
        }
      }
      createdAt
      updatedAt
    }
  }
`;

export const FIND_UNIQUE_URL_CONFIG = gql`
  query FindUniqueUrlConfig($args: JSONObject!) {
    findUniqueUrlConfig(args: $args) {
      id
      displayUrl
      apiEndpoint
      apiClientId
      folderName
      folderId
      lastSyncAt
      folder {
        id
        name
        description
      }
      createdAt
      updatedAt
    }
  }
`;

export const GET_URL_CONFIGS_COUNT = gql`
  query GetUrlConfigsCount {
    getUrlConfigsCount
  }
`;

export const GET_URL_FOLDERS_FOR_AGENT = gql`
  query GetUrlFoldersForAgent {
    getUrlFoldersForAgent {
      id
      displayUrl
      folderName
      folderId
      folder {
        id
        name
        description
      }
    }
  }
`;

export const CREATE_URL_CONFIG = gql`
  mutation CreateUrlConfig($displayUrl: String!, $apiEndpoint: String, $folderName: String, $userId: String) {
    createUrlConfig(displayUrl: $displayUrl, apiEndpoint: $apiEndpoint, folderName: $folderName, userId: $userId) {
      id
      displayUrl
      apiEndpoint
      folderName
      folderId
      lastSyncAt
      folder {
        id
        name
        description
      }
      createdAt
    }
  }
`;

export const DELETE_ONE_URL_CONFIG = gql`
  mutation DeleteOneUrlConfig($args: JSONObject!) {
    deleteOneUrlConfig(args: $args) {
      id
      displayUrl
    }
  }
`;

export const VALIDATE_URL_CONFIG = gql`
  mutation ValidateUrlConfig($displayUrl: String!, $apiEndpoint: String) {
    validateUrlConfig(displayUrl: $displayUrl, apiEndpoint: $apiEndpoint) {
      valid
      accessible
      error
    }
  }
`;

export const CREATE_URL_AGENT = gql`
  mutation CreateUrlAgent($createAgentDto: CreateAgentDto!) {
    createUserAgent(createAgentDto: $createAgentDto) {
      id
      name
      status
      agentType
      languageId
      userId
      workflowId
      createdAt
      updatedAt
    }
  }
`;

export const CREATE_URL_AGENT_SYNC_CONFIG = gql`
  mutation CreateUrlAgentSyncConfig($args: JSONObject!) {
    createAgentSyncConfig(args: $args) {
      id
      agentId
      syncUrls
      folderId
      autoUpdate
      lastSyncAt
      createdAt
      updatedAt
    }
  }
`;

// ── UEH Article Sync (from ueh_chatbot) ─────────────────────────────────────

export const GET_UEH_ARTICLES = gql`
  query GetUehArticles($skip: Int, $take: Int, $search: String) {
    getUehArticles(skip: $skip, take: $take, search: $search) {
      id
      linkNguon
      title
      menu
      sourceUrl
      htmlContent
      markdownContent
      lastUpdatedAt
      weaviateId
      status
      createdAt
      updatedAt
    }
  }
`;

export const GET_UEH_ARTICLES_COUNT = gql`
  query GetUehArticlesCount($search: String) {
    getUehArticlesCount(search: $search)
  }
`;

export const GET_ALL_UEH_ARTICLES_SOURCE_URLS = gql`
  query GetAllUehArticlesSourceUrls {
    getUehArticles(skip: 0, take: 10000) {
      sourceUrl
    }
  }
`;

export const GET_ARTICLES_BY_AGENT = gql`
  query GetArticlesByAgent($agentId: String!) {
    getArticlesByAgent(agentId: $agentId) {
      id
      linkNguon
      title
      menu
      htmlContent
      markdownContent
      lastUpdatedAt
      weaviateId
      status
      sourceUrl
      agentId
      createdAt
      updatedAt
    }
  }
`;

export const GET_CHATBOT_UPDATE_STATUS = gql`
  query GetChatbotUpdateStatus($agentId: String!) {
    getChatbotUpdateStatus(agentId: $agentId)
  }
`;

export const GET_LAST_WEEKDAY_DATE = gql`
  query GetLastWeekdayDate {
    getLastWeekdayDate
  }
`;

export const GET_RECENT_UEH_SYNC_JOBS = gql`
  query GetRecentUehSyncJobs {
    getRecentUehSyncJobs {
      id
      status
      progress
      data
      result
      error
      createdAt
      completedAt
    }
  }
`;

export const GET_UEH_SYNC_STATUS = gql`
  query GetUehSyncStatus($jobId: String!) {
    getUehSyncStatus(jobId: $jobId) {
      id
      status
      progress
      data
      result
      error
      createdAt
      completedAt
    }
  }
`;

export const GET_UEH_SYNC_QUEUE_STATS = gql`
  query GetUehSyncQueueStats {
    getUehSyncQueueStats {
      waiting
      active
      completed
      failed
      delayed
      stuck
    }
  }
`;

export const GET_SYNC_STATISTICS = gql`
  query GetSyncStatistics($agentId: String, $dateFrom: String, $dateTo: String) {
    getSyncStatistics(agentId: $agentId, dateFrom: $dateFrom, dateTo: $dateTo) {
      totalSyncs
      successfulSyncs
      failedSyncs
      totalArticlesProcessed
      totalArticlesAdded
      totalArticlesUpdated
      totalArticlesDeleted
      averageSyncDuration
      lastSyncAt
      syncsByDate {
        date
        count
        success
      }
    }
  }
`;

export const START_UEH_SYNC = gql`
  mutation StartUehSync {
    startUehSync
  }
`;

export const START_UEH_SYNC_JOB = gql`
  mutation StartUehSyncJob($requestedBy: String) {
    startUehSyncJob(requestedBy: $requestedBy)
  }
`;

export const CANCEL_UEH_SYNC_JOB = gql`
  mutation CancelUehSyncJob($jobId: String!) {
    cancelUehSyncJob(jobId: $jobId)
  }
`;

export const INIT_DEFAULT_UEH_CONFIG = gql`
  mutation InitializeDefaultUehConfig($userId: String) {
    initializeDefaultUehConfig(userId: $userId) {
      id
      displayUrl
      apiEndpoint
      folderName
      folderId
      folder {
        id
        name
        description
      }
      createdAt
    }
  }
`;

export const EXPORT_ARTICLES_TO_DOCUMENTS = gql`
  mutation ExportArticlesToDocuments($urlConfigId: String!, $agentId: String) {
    exportArticlesToDocuments(urlConfigId: $urlConfigId, agentId: $agentId) {
      success
      documentsCreated
      documentsUpdated
      documentsDeleted
      totalProcessed
      folderId
      error
    }
  }
`;

// ── URL Sync Operations (from ueh_chatbot) ──────────────────────────────────

export const GET_SUGGESTED_SYNC_DATES = gql`
  query GetSuggestedSyncDates {
    getSuggestedSyncDates
  }
`;

export const GET_SYNCABLE_URLS = gql`
  query GetSyncableUrls {
    getSyncableUrls {
      url
      name
      description
      active
    }
  }
`;

export const GET_MULTI_URL_SYNC_STATUS = gql`
  query GetMultiUrlSyncStatus($jobId: String!) {
    getUehSyncStatus(jobId: $jobId) {
      id
      status
      progress
      data
      result
      error
      createdAt
      completedAt
    }
  }
`;

export const VALIDATE_SYNC_URLS = gql`
  query ValidateSyncUrls($urls: [String!]!) {
    validateSyncUrls(urls: $urls) {
      url
      valid
      accessible
      responseTime
      error
    }
  }
`;

export const SYNC_URL_CONFIG = gql`
  mutation SyncUrlConfig($id: String!, $userId: String) {
    syncUrlConfig(id: $id, userId: $userId) {
      success
      articlesCount
      newCount
      updatedCount
      url
      error
      syncDate
    }
  }
`;

export const SYNC_URL_CONFIG_FOR_DATE = gql`
  mutation SyncUrlConfigForDate($id: String!, $date: String!, $userId: String) {
    syncUrlConfigForDate(id: $id, date: $date, userId: $userId) {
      success
      articlesCount
      newCount
      updatedCount
      url
      error
      syncDate
    }
  }
`;

export const SYNC_URL_CONFIG_DATE_RANGE = gql`
  mutation SyncUrlConfigDateRange($id: String!, $startDate: String!, $endDate: String!) {
    syncUrlConfigDateRange(id: $id, startDate: $startDate, endDate: $endDate) {
      success
      totalArticles
      totalNew
      totalUpdated
      syncedDates
      error
    }
  }
`;

export const SYNC_MULTIPLE_URLS = gql`
  mutation SyncMultipleUrls($displayUrls: [String!]!) {
    syncMultipleUrls(displayUrls: $displayUrls) {
      success
      articlesCount
      newCount
      updatedCount
      url
      error
    }
  }
`;

export const START_UEH_SYNC_FOR_DATE = gql`
  mutation StartUehSyncForDate($date: String!) {
    startUehSyncForDate(date: $date)
  }
`;

export const START_UEH_SYNC_FOR_DATE_RANGE = gql`
  mutation StartUehSyncForDateRange($startDate: String!, $endDate: String!) {
    startUehSyncForDateRange(startDate: $startDate, endDate: $endDate)
  }
`;

export const START_UEH_SYNC_JOB_FOR_DATE = gql`
  mutation StartUehSyncJobForDate($date: String!, $requestedBy: String) {
    startUehSyncJobForDate(date: $date, requestedBy: $requestedBy)
  }
`;

export const START_UEH_SYNC_JOB_FOR_DATE_RANGE = gql`
  mutation StartUehSyncJobForDateRange($startDate: String!, $endDate: String!, $requestedBy: String) {
    startUehSyncJobForDateRange(startDate: $startDate, endDate: $endDate, requestedBy: $requestedBy)
  }
`;

export const START_CONNECTION_TEST_JOB = gql`
  mutation StartConnectionTestJob($requestedBy: String) {
    startConnectionTestJob(requestedBy: $requestedBy)
  }
`;

export const START_MULTI_URL_SYNC_JOB = gql`
  mutation StartMultiUrlSyncJob($urls: [String!]!, $targetDate: String, $startDate: String, $endDate: String, $requestedBy: String) {
    startMultiUrlSyncJob(urls: $urls, targetDate: $targetDate, startDate: $startDate, endDate: $endDate, requestedBy: $requestedBy)
  }
`;

export const SYNC_AND_UPDATE_AGENT = gql`
  mutation SyncAndUpdateAgent($agentId: String!, $syncUrls: [String!]!, $targetDate: String) {
    syncAndUpdateAgent(agentId: $agentId, syncUrls: $syncUrls, targetDate: $targetDate)
  }
`;

export const UPDATE_AGENTS_WITH_SYNCED_DATA = gql`
  mutation UpdateAgentsWithSyncedData($agentIds: [String!]!, $urls: [String!], $requestedBy: String) {
    updateAgentsWithSyncedData(agentIds: $agentIds, urls: $urls, requestedBy: $requestedBy) {
      success
      agentId
      exportResult {
        success
        documentsCreated
        documentsUpdated
        documentsDeleted
        totalProcessed
        folderId
        error
      }
      error
    }
  }
`;
